import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import api from '../../utils/api';
import { ClipboardDocumentListIcon, PlusIcon, UsersIcon, CheckCircleIcon, ChartPieIcon } from '@heroicons/react/24/outline';
import StatCard from './StatCard';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch projects
        const projectsResponse = await api.get('/projects');
        console.log('Projects response:', projectsResponse.data);
        
        // Handle different response formats
        let projectsData = [];
        if (Array.isArray(projectsResponse.data)) {
          projectsData = projectsResponse.data;
        } else if (projectsResponse.data && Array.isArray(projectsResponse.data.projects)) {
          projectsData = projectsResponse.data.projects;
        } else if (projectsResponse.data && typeof projectsResponse.data === 'object') {
          console.warn('Unexpected projects response format:', projectsResponse.data);
          projectsData = [];
        }
        
        setProjects(projectsData);
        
        // Fetch clients
        const clientsResponse = await api.get('/clients');
        console.log('Clients response:', clientsResponse.data);
        
        // Handle different response formats
        let clientsData = [];
        if (Array.isArray(clientsResponse.data)) {
          clientsData = clientsResponse.data;
        } else if (clientsResponse.data && Array.isArray(clientsResponse.data.clients)) {
          clientsData = clientsResponse.data.clients;
        } else if (clientsResponse.data && typeof clientsResponse.data === 'object') {
          console.warn('Unexpected clients response format:', clientsResponse.data);
          clientsData = [];
        }
        
        setClients(clientsData);
        
        // Fetch available status types for projects
        const statusesResponse = await api.get('/settings/status-types/project');
        if (statusesResponse.data && statusesResponse.data.statusTypes) {
          setAvailableStatuses(statusesResponse.data.statusTypes);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Calculate statistics - ensure projects is an array first
  const projectsArray = Array.isArray(projects) ? projects : [];
  
  // Find 'Completed' status type
  const completedStatus = availableStatuses.find(status => status.name === 'Completed')?.name || 'Completed';
  
  // Consider all non-completed projects as active
  const activeProjects = projectsArray.filter(p => p && p.status !== completedStatus).length;
  const completedProjects = projectsArray.filter(p => p && p.status === completedStatus).length;
  
  const totalClients = Array.isArray(clients) ? clients.length : 0;
  const completionRate = projectsArray.length > 0 
    ? Math.round((completedProjects / projectsArray.length) * 100) 
    : 0;
  
  // Get 5 most recent projects
  const recentProjects = projectsArray.length > 0
    ? [...projectsArray]
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 5)
    : [];
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{error}</h3>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-white">Dashboard</h1>
          <p className="mt-2 text-sm text-gray-300">
            Overview of your project management activities
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-4">
          <Link
            to="/projects/add"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Project
          </Link>
          <Link
            to="/clients/add"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Client
          </Link>
        </div>
      </div>
      
      {/* Stats Section */}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard 
          title="Active Projects" 
          value={activeProjects.toString()}
          description="Current ongoing projects"
          icon={<ClipboardDocumentListIcon className="h-6 w-6 text-white" aria-hidden="true" />}
          iconBackground="bg-indigo-500"
        />
        <StatCard 
          title="Completed Projects" 
          value={completedProjects.toString()}
          description="Successfully delivered"
          icon={<CheckCircleIcon className="h-6 w-6 text-white" aria-hidden="true" />}
          iconBackground="bg-green-500"
        />
        <StatCard 
          title="Total Clients" 
          value={totalClients.toString()}
          description="Across all projects"
          icon={<UsersIcon className="h-6 w-6 text-white" aria-hidden="true" />}
          iconBackground="bg-blue-500"
        />
        <StatCard 
          title="Project Completion Rate" 
          value={`${completionRate}%`}
          description="Overall completion rate"
          icon={<ChartPieIcon className="h-6 w-6 text-white" aria-hidden="true" />}
          iconBackground="bg-purple-500"
        />
      </div>
      
      {/* Recent Projects Section */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h2 className="text-xl font-semibold text-white">Recent Projects</h2>
            <p className="mt-2 text-sm text-gray-300">
              Your most recently created projects
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <Link
              to="/projects"
              className="inline-flex items-center justify-center rounded-md border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            >
              View All
            </Link>
          </div>
        </div>
        
        <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          {recentProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 bg-gray-800">
              <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-200">No projects yet</h3>
              <p className="mt-1 text-sm text-gray-400">Get started by creating a new project</p>
              <div className="mt-6">
                <Link
                  to="/projects/add"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Add Project
                </Link>
              </div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-700">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-200 sm:pl-6">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-200">
                    Client
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-200">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-200">
                    Created
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">View</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600 bg-gray-800">
                {recentProjects.map((project) => {
                  const client = Array.isArray(clients) ? clients.find(c => c && c.id === project.client_id) : null;
                  const status = project.status || 'unknown';
                  const formattedDate = project.created_at ? format(new Date(project.created_at), 'MMM d, yyyy') : 'Unknown date';
                  
                  return (
                    <tr key={project.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-200 sm:pl-6">
                        {project.name || 'Unnamed Project'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                        {client ? client.name : 'Unknown Client'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                        <span 
                          className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 items-center justify-center"
                          style={{
                            backgroundColor: 
                              status === completedStatus ? '#10B981' :  // Green for completed
                              status === 'In Progress' ? '#3B82F6' :   // Blue for in progress
                              status === 'Canceled' ? '#EF4444' :      // Red for canceled
                              status === 'Paused' ? '#F59E0B' :        // Orange for paused
                              '#6B7280',                               // Gray for others
                            color: 'white',
                            minWidth: '80px',
                            textAlign: 'center'
                          }}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                        {formattedDate}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <Link 
                          to={`/projects/${project.id}`}
                          className="text-indigo-400 hover:text-indigo-300"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 